// Castintech dashboard — Org Chart view modülü.
// Aşama F (2026-05-25) — CHARTER v1.3 § 2 hiyerarşi + ajan durum kartları.
// Aşama G G3 (2026-05-29, BD-2026-05-28-002) — efqm-gm CQO org şemasına eklendi (CHARTER v1.4).
// Veri:
//   - Hiyerarşi: CHARTER § 2 statik (Yılmaz → Nexus Süper-GM → 4 alt-GM)
//   - Ajan durum: state.agentsFull (getAgentsFull() ile yüklenir)
//
// Tasarım: Dikey ağaç (CEO → Süper-GM → 4 GM → alt-ajanlar collapsible).
// Tıklama: ajan kartı → side panel (AGENTS.md özet render edilir — ileride eklenecek).

import { apiFetch } from '../api.js';
import { state } from '../state.js';
import { escHtml, escAttr, $ } from '../utils.js';
import { createSkeleton, renderError } from '../ui/state-display.js';

// CHARTER v1.4 § 2 statik hiyerarşi (2026-05-29).
// Aşama G G3 (2026-05-29, BD-2026-05-28-002) — efqm-gm CQO Sentinel altına eklendi.
// Güncelleme: agent-architecture.md değişince burası da güncellenmeli.
interface OrgNode {
  slug: string;
  name: string;
  title: string;
  gem: string;
  efqm: number | null;
  model: string;
  status: 'active' | 'activating' | 'paused' | 'system';
  reports_to: string | null;
  children?: OrgNode[];
}

const ORG_TREE: OrgNode = {
  slug: 'yilmaz',
  name: 'Baş Yılmaz',
  title: 'CEO · Chairman of Board',
  gem: '👤',
  efqm: null,
  model: 'insan',
  status: 'active',
  reports_to: null,
  children: [
    {
      slug: 'nexus',
      name: 'Nexus',
      title: 'Süper-GM · Castagent Platform GM',
      gem: '🔷',
      efqm: 1,
      model: 'Sonnet 4.6',
      status: 'active',
      reports_to: 'yilmaz',
      children: [
        // Platform alt-ajanları (Nexus kendi domain'i)
        {
          slug: 'forge',
          name: 'Forge',
          title: 'Platform Dev',
          gem: '⚒️',
          efqm: 3,
          model: 'Sonnet 4.6',
          status: 'active',
          reports_to: 'nexus',
        },
        {
          slug: 'pilot',
          name: 'Pilot',
          title: 'Deploy & Infra',
          gem: '🛬',
          efqm: 3,
          model: 'Sonnet 4.6',
          status: 'active',
          reports_to: 'nexus',
        },
        {
          slug: 'prism',
          name: 'Prism',
          title: 'CTO · Platform Mimarisi',
          gem: '🔺',
          efqm: 3,
          model: 'Sonnet 4.6',
          status: 'active',
          reports_to: 'nexus',
        },
        {
          slug: 'lore',
          name: 'Lore',
          title: 'Araştırma Uzmanı (Havuz)',
          gem: '📖',
          efqm: 4,
          model: 'Haiku 4.5',
          status: 'active',
          reports_to: 'nexus',
        },
        {
          slug: 'maestro',
          name: 'Maestro',
          title: 'CTOO · Eko-sistem Beyni',
          gem: '🎼',
          efqm: 2,
          model: 'Opus 4.7',
          status: 'active',
          reports_to: 'nexus',
        },
        // 4 alt-GM'ler
        {
          slug: 'sentinel',
          name: 'Sentinel',
          title: 'Castintech GM',
          gem: '🛡️',
          efqm: 2,
          model: 'Sonnet 4.6',
          status: 'active',
          reports_to: 'nexus',
          children: [
            { slug: 'atlas', name: 'Atlas', title: 'COO · Müşteri Ops', gem: '🌍', efqm: 1, model: 'Sonnet 4.6', status: 'active', reports_to: 'sentinel' },
            { slug: 'pearl', name: 'Pearl', title: 'CCO · Onboarding', gem: '🦪', efqm: 1, model: 'Sonnet 4.6', status: 'active', reports_to: 'sentinel' },
            { slug: 'onyx', name: 'Onyx', title: 'GMY · Model & Disiplin', gem: '🪨', efqm: 4, model: 'Haiku 4.5', status: 'active', reports_to: 'sentinel' },
            { slug: 'topaz', name: 'Topaz', title: 'Reklam Uzmanı (Havuz)', gem: '🟡', efqm: 3, model: 'Haiku 4.5', status: 'active', reports_to: 'sentinel' },
            { slug: 'opal', name: 'Opal', title: 'Kreatif Uzman (Havuz)', gem: '🔮', efqm: 3, model: 'Haiku 4.5', status: 'active', reports_to: 'sentinel' },
            { slug: 'vanta', name: 'Vanta', title: 'CSO · Strateji', gem: '🌑', efqm: 2, model: 'Opus 4.7', status: 'active', reports_to: 'sentinel' },
            { slug: 'herald', name: 'Herald', title: 'İletişim & Legalite', gem: '📯', efqm: 1, model: 'Haiku 4.5', status: 'active', reports_to: 'sentinel' },
            { slug: 'sapphire', name: 'Sapphire', title: 'CFO', gem: '💠', efqm: 4, model: 'Sonnet 4.6', status: 'active', reports_to: 'sentinel' },
            { slug: 'ruby', name: 'Ruby', title: 'Satış & Büyüme', gem: '❤️', efqm: 1, model: 'Haiku 4.5', status: 'active', reports_to: 'sentinel' },
            { slug: 'meter', name: 'Meter', title: 'Token Takip', gem: '⏱️', efqm: 4, model: 'Haiku 4.5', status: 'system', reports_to: 'sentinel' },
            // Aşama G G3 (2026-05-29, BD-2026-05-28-002) — efqm-gm CQO org şemasına eklendi.
            // CHARTER v1.4 § 2: Sentinel altında (Meter ile CFO/Sapphire slot arası), cross-line Pearl + Maestro.
            { slug: 'efqm-gm', name: 'efqm-gm', title: 'CQO — Kalite + Müşteri Memnuniyeti + EFQM', gem: '💎', efqm: 5, model: 'Sonnet 4.6', status: 'active', reports_to: 'sentinel' },
            { slug: 'watchdog', name: 'Watchdog', title: 'Kilitlenme İzleme', gem: '🐕', efqm: 5, model: 'Haiku 4.5', status: 'system', reports_to: 'sentinel' },
          ],
        },
        {
          slug: 'castmart-gm',
          name: 'Castmart-GM',
          title: 'Castmart GM',
          gem: '🛒',
          efqm: 2,
          model: 'Sonnet 4.6',
          status: 'active',
          reports_to: 'nexus',
          children: [
            { slug: 'castmart-lead', name: 'Castmart-Lead', title: 'Product Lead', gem: '📦', efqm: 3, model: 'Sonnet 4.6', status: 'active', reports_to: 'castmart-gm' },
          ],
        },
        {
          slug: 'iq7s-gm',
          name: 'IQ7s-GM',
          title: 'IQ7s GM',
          gem: '🧠',
          efqm: 2,
          model: 'Sonnet 4.6',
          status: 'active',
          reports_to: 'nexus',
          children: [
            { slug: 'iq7s-lead', name: 'IQ7s-Lead', title: 'Product Lead', gem: '💡', efqm: 3, model: 'Sonnet 4.6', status: 'active', reports_to: 'iq7s-gm' },
          ],
        },
        {
          slug: 'castacc-gm',
          name: 'Castacc-GM',
          title: 'Castacc GM (Pause)',
          gem: '📊',
          efqm: 2,
          model: 'Sonnet 4.6',
          status: 'paused',
          reports_to: 'nexus',
        },
      ],
    },
  ],
};

// Ajan durum badge
function statusBadge(slug: string): string {
  // state.agentsFull'dan gerçek durumu çek (varsa)
  const agent = state.agentsFull.find((a: any) => a.slug === slug || a.name?.toLowerCase() === slug.toLowerCase());
  if (!agent) return '';
  const s = (agent.status || '').toLowerCase();
  if (s === 'active' || s === 'running') return '<span class="oc-status oc-status-active" title="Aktif">●</span>';
  if (s === 'busy' || s === 'working') return '<span class="oc-status oc-status-busy" title="Meşgul">◉</span>';
  if (s === 'error' || s === 'failed') return '<span class="oc-status oc-status-error" title="Hata">✕</span>';
  if (s === 'paused') return '<span class="oc-status oc-status-paused" title="Duraklatıldı">⏸</span>';
  return '';
}

function renderNode(node: OrgNode, depth = 0): string {
  const hasChildren = node.children && node.children.length > 0;
  const isHuman = node.slug === 'yilmaz';
  const isPaused = node.status === 'paused';
  const isSystem = node.status === 'system';

  const depthClass = `oc-depth-${Math.min(depth, 3)}`;
  const cardClass = [
    'oc-card',
    depthClass,
    isPaused ? 'oc-paused' : '',
    isHuman ? 'oc-human' : '',
    isSystem ? 'oc-system' : '',
  ].filter(Boolean).join(' ');

  const efqmBadge = node.efqm ? `<span class="oc-efqm" title="EFQM Kriter ${node.efqm}">K${node.efqm}</span>` : '';
  const liveStatus = statusBadge(node.slug);

  const card = `
    <div class="${cardClass}" data-slug="${escAttr(node.slug)}" role="treeitem" aria-expanded="${hasChildren ? 'true' : undefined}" tabindex="0">
      <div class="oc-card-inner">
        <div class="oc-gem" aria-hidden="true">${node.gem}</div>
        <div class="oc-info">
          <div class="oc-name">${escHtml(node.name)}${liveStatus}</div>
          <div class="oc-title">${escHtml(node.title)}</div>
          <div class="oc-meta">${efqmBadge}<span class="oc-model">${escHtml(node.model)}</span></div>
        </div>
        ${hasChildren ? '<span class="oc-toggle" aria-hidden="true">▾</span>' : ''}
      </div>
    </div>`;

  if (!hasChildren) return card;

  const childrenHtml = node.children!.map(c => renderNode(c, depth + 1)).join('');
  return `
    <div class="oc-branch">
      ${card}
      <div class="oc-children" role="group">${childrenHtml}</div>
    </div>`;
}

let _ocLastError: string | null = null;
let _ocLoaded = false;

export async function loadOrgChart(): Promise<void> {
  // F4-A (2026-05-26): ilk yüklemede skeleton göster
  if (!_ocLoaded && state.currentPage === 'org-chart') {
    const root = $('org-chart-content');
    if (root) {
      root.innerHTML = '';
      const sk = createSkeleton({ rows: 4, height: '64px', rowGap: '12px' });
      root.appendChild(sk);
    }
  }
  if (!state.agentsFull.length) {
    try {
      const r = await apiFetch('/castintech-api/agents/full');
      if (r.ok) {
        const d = await r.json() as { agents?: unknown[] } | unknown[];
        state.agentsFull = (Array.isArray(d) ? d : ((d as any).agents || [])) as any[];
        _ocLastError = null;
      } else {
        _ocLastError = 'agents/full HTTP ' + r.status;
      }
    } catch (e) {
      _ocLastError = (e as Error).message || 'Ajan durum verisi alınamadı';
    }
  }
  _ocLoaded = true;
  if (state.currentPage === 'org-chart') renderOrgChart();
}

export function renderOrgChart(): void {
  const root = $('org-chart-content');
  if (!root) return;

  const sub = $('org-chart-sub');
  if (sub) sub.textContent = 'CHARTER v1.3 § 2 — Yılmaz → Nexus (Süper-GM) → 4 alt-GM';

  // F4-A (2026-05-26): hata durumunda görünür retry mesajı
  if (_ocLastError && !state.agentsFull.length) {
    root.innerHTML = '';
    renderError(root, _ocLastError, {
      title: 'Ajan durum verisi yüklenirken sorun oluştu',
      description: _ocLastError,
      retryLabel: 'Tekrar dene',
      onRetry: () => loadOrgChart(),
    });
    return;
  }

  root.innerHTML = `
    <div class="oc-tree" role="tree" aria-label="Castintech ajan hiyerarşisi">
      ${renderNode(ORG_TREE)}
    </div>
    <div class="oc-legend">
      <span class="oc-status oc-status-active">●</span> Aktif &nbsp;
      <span class="oc-status oc-status-busy">◉</span> Meşgul &nbsp;
      <span class="oc-status oc-status-error">✕</span> Hata &nbsp;
      <span class="oc-status oc-status-paused">⏸</span> Pause &nbsp;
      <span class="oc-efqm">K1</span> EFQM Kriteri
    </div>`;

  // Collapse/expand toggle
  root.querySelectorAll<HTMLElement>('.oc-card[data-slug]').forEach(card => {
    card.addEventListener('click', () => {
      const branch = card.closest('.oc-branch');
      if (!branch) return;
      const children = branch.querySelector('.oc-children') as HTMLElement | null;
      if (!children) return;
      const collapsed = children.style.display === 'none';
      children.style.display = collapsed ? '' : 'none';
      card.setAttribute('aria-expanded', String(collapsed));
      const toggle = card.querySelector('.oc-toggle');
      if (toggle) toggle.textContent = collapsed ? '▾' : '▸';
    });
    // Klavye a11y
    card.addEventListener('keydown', (e: KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); card.click(); }
    });
  });
}
